import React, { useEffect, useState, useCallback } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';

/**
 * Floating "Install App" prompt for PWA installation.
 *
 *  - Android/Chrome/Edge: listens for `beforeinstallprompt` and triggers the native install dialog.
 *  - iOS Safari: shows a one-time hint explaining Share → Add to Home Screen
 *    (iOS doesn't expose `beforeinstallprompt`).
 *  - Hides itself permanently after install OR if the user dismisses (stored in localStorage).
 *  - Auto-hides if the app is already running standalone.
 */
const STORAGE_KEY = 'credys-pwa-install-dismissed';

const isStandalone = () =>
  (typeof window !== 'undefined') &&
  (window.matchMedia('(display-mode: standalone)').matches ||
   window.navigator.standalone === true);

const isIOS = () => {
  const ua = (navigator.userAgent || '').toLowerCase();
  return /iphone|ipad|ipod/.test(ua) && !/crios|fxios/.test(ua);
};

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  const dismiss = useCallback(() => {
    setShow(false);
    setIosHint(false);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
  }, []);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') return; // dismissed earlier
    } catch {}

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };

    const onInstalled = () => {
      try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
      setShow(false);
      setIosHint(false);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    // iOS: show hint after a short delay (only if not dismissed and not standalone)
    if (isIOS()) {
      const t = setTimeout(() => setIosHint(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onPrompt);
        window.removeEventListener('appinstalled', onInstalled);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
    }
    setDeferredPrompt(null);
    setShow(false);
  };

  if (!show && !iosHint) return null;

  // Android / Chrome / Edge prompt
  if (show && deferredPrompt) {
    return (
      <div
        className="fixed bottom-20 lg:bottom-6 inset-x-3 lg:inset-x-auto lg:right-6 lg:max-w-sm z-50 border-2 shadow-2xl"
        style={{ backgroundColor: '#00395d', borderColor: '#00aeef' }}
        data-testid="pwa-install-prompt"
      >
        <div className="px-4 py-3 flex items-start gap-3">
          <div className="w-10 h-10 flex items-center justify-center border-2 flex-shrink-0" style={{ borderColor: '#00aeef', backgroundColor: 'rgba(0,174,239,0.15)' }}>
            <Download className="w-5 h-5" style={{ color: '#00aeef' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1" style={{ color: '#00aeef' }}>Install Credys</p>
            <p className="text-sm font-bold text-white leading-tight">Add to Home Screen</p>
            <p className="text-xs mt-1" style={{ color: '#8fc8e8' }}>Launch instantly. Works offline. No app store needed.</p>
          </div>
          <button onClick={dismiss} className="text-white/60 hover:text-white p-1" aria-label="Dismiss" data-testid="pwa-install-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 border-t-2" style={{ borderColor: '#00aeef33' }}>
          <button
            onClick={dismiss}
            className="py-2.5 text-[11px] font-bold uppercase tracking-wider text-white/70 hover:bg-white/5 transition-colors"
            data-testid="pwa-install-later"
          >
            Maybe Later
          </button>
          <button
            onClick={install}
            className="py-2.5 text-[11px] font-bold uppercase tracking-wider transition-colors"
            style={{ backgroundColor: '#00aeef', color: '#fff' }}
            data-testid="pwa-install-confirm"
          >
            Install Now
          </button>
        </div>
      </div>
    );
  }

  // iOS hint
  if (iosHint) {
    return (
      <div
        className="fixed bottom-20 lg:bottom-6 inset-x-3 lg:inset-x-auto lg:right-6 lg:max-w-sm z-50 border-2 shadow-2xl"
        style={{ backgroundColor: '#00395d', borderColor: '#00aeef' }}
        data-testid="pwa-ios-hint"
      >
        <div className="px-4 py-3 flex items-start gap-3">
          <div className="w-10 h-10 flex items-center justify-center border-2 flex-shrink-0" style={{ borderColor: '#00aeef', backgroundColor: 'rgba(0,174,239,0.15)' }}>
            <Download className="w-5 h-5" style={{ color: '#00aeef' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1" style={{ color: '#00aeef' }}>Install Credys</p>
            <p className="text-sm font-bold text-white leading-tight">Add to Home Screen</p>
            <p className="text-xs mt-1.5" style={{ color: '#8fc8e8' }}>
              Tap <Share className="inline w-3.5 h-3.5 mx-0.5 align-text-bottom" /> Share, then{' '}
              <span className="inline-flex items-center gap-0.5 font-bold text-white">
                <Plus className="w-3 h-3" /> Add to Home Screen
              </span>.
            </p>
          </div>
          <button onClick={dismiss} className="text-white/60 hover:text-white p-1" aria-label="Dismiss" data-testid="pwa-ios-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default PWAInstallPrompt;
